import {
  ActionAgent,
  ActionTarget,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import {
  componentsWithDisable,
  componentsWithHide,
} from '@flowx/designer/shared/entities/template-config.entity'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { NavigationActions } from './app-navigation.service'

const getAiDeveloperNavigationTarget = (
  action: AgentAction,
  target: ActionTarget
): string | null => {
  switch (action) {
    case AgentAction.BusinessRule:
      return NavigationActions.showBrArea
    case AgentAction.Expression:
      return getExpressionNavigationTarget(target)
    case AgentAction.CustomComponent:
      return NavigationActions.openTestCustomComponentModal
    case AgentAction.ReusableFunction:
    case AgentAction.EditReusableFunction:
      return NavigationActions.openTestReusableFunctionModal
    default:
      return null
  }
}
const getExpressionNavigationTarget = (target: ActionTarget): string | null => {
  const expressionType = target.expression?.expressionType
  switch (expressionType) {
    case ConversationThreadExecutionPlanActionTargetExpressionType.Hide:
      return NavigationActions.showHideExpressionArea
    case ConversationThreadExecutionPlanActionTargetExpressionType.Disable:
      return NavigationActions.showDisableExpressionArea
    default:
      return null
  }
}
export const getNavigationTarget = (agent: ActionAgent, target: ActionTarget): string | null => {
  switch (agent.agent) {
    case AgentName.AiAnalyst:
      return NavigationActions.openProcess
    case AgentName.AiDesigner:
      return (agent.action as AgentAction) === AgentAction.CreateUiTemplate
        ? NavigationActions.openReusableUiTemplate
        : NavigationActions.openNode
    case AgentName.AiDeveloper:
      return getAiDeveloperNavigationTarget(agent.action as AgentAction, target)
    case AgentName.AiArchitect:
      return NavigationActions.openProjectDataModel
    default:
      return null
  }
}

export const getTargetNode = (
  action: {
    agent: ActionAgent
    target: ActionTarget
  },
  nodes: NodeDefinition[]
): NodeDefinition | null => {
  if (action.agent.agent === AgentName.AiDesigner) {
    return nodes.find((node) => node.id === parseInt(action.target?.node?.id, 10))
  }

  if (action.agent.agent === AgentName.AiDeveloper) {
    if (
      [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(
        action.agent.action as AgentAction
      )
    ) {
      // Try businessRule.nodeId first, then fallback to node.id for partial targets
      const nodeId = action.target?.businessRule?.nodeId || action.target?.node?.id
      return nodes.find((node) => node.id === parseInt(nodeId, 10))
    }

    if (action.agent.action === AgentAction.Expression) {
      // Try expression.nodeId first, then fallback to node.id for partial targets
      const nodeId = action.target?.expression?.nodeId || action.target?.node?.id
      return nodes.find((node) => node.id === parseInt(nodeId, 10))
    }

    return null
  }

  return null
}

const isNodeTargetAvailable = (target: ActionTarget, nodes: NodeDefinition[]): boolean => {
  if (!target.node?.id) {
    return false
  }
  const nodeId = parseInt(target.node.id, 10)
  return nodes.some((node) => node.id === nodeId)
}

const isBusinessRuleTargetAvailable = (target: ActionTarget, nodes: NodeDefinition[]): boolean => {
  if (!target.businessRule?.nodeId) {
    return false
  }

  const nodeId = parseInt(target.businessRule.nodeId, 10)
  const node = nodes.find((n) => n.id === nodeId)

  if (!node) {
    return false
  }

  if (target.businessRule.actionId) {
    const actionId = parseInt(target.businessRule.actionId, 10)
    const action = node.actionDefinitions?.find((a) => a.id === actionId)

    if (!action) {
      return false
    }

    if (target.businessRule.index >= 0) {
      const businessRules = action.actionRuleDefinitions || []
      return target.businessRule.index < businessRules.length
    }
  }

  return true
}

const isExpressionTargetAvailable = (
  target: ActionTarget,
  nodes: NodeDefinition[],
  templateConfigsMap?: Record<number, TemplateConfigInterface>
): boolean => {
  if (!target.expression?.nodeId || !target.expression?.elementId) {
    return false
  }

  const nodeId = parseInt(target.expression.nodeId, 10)
  const node = nodes.find((n) => n.id === nodeId)

  if (!node) {
    return false
  }

  if (templateConfigsMap) {
    const elementId = parseInt(target.expression.elementId, 10)
    const elementConfig = templateConfigsMap[elementId]

    if (!elementConfig) {
      return false
    }

    const expressionType = target.expression.expressionType

    if (
      expressionType === ConversationThreadExecutionPlanActionTargetExpressionType.Hide &&
      !componentsWithHide.includes(elementConfig.componentIdentifier)
    ) {
      return false
    }

    if (
      expressionType === ConversationThreadExecutionPlanActionTargetExpressionType.Disable &&
      !componentsWithDisable.includes(elementConfig.componentIdentifier)
    ) {
      return false
    }
  }

  return true
}

const isProcessTargetAvailable = (): boolean => true

// The project data model is the project root; it exists as long as the project does, so the
// pending action's destination can never be "deleted" out from under a restored thread.
const isProjectDataModelTargetAvailable = (): boolean => true

const isCustomComponentTargetAvailable = (target: ActionTarget): boolean =>
  !!target.customComponent?.identifier

const isReusableFunctionTargetAvailable = (target: ActionTarget): boolean => !!target.name

const isReusableUiTemplateTargetAvailable = (target: ActionTarget): boolean => !!target.name

export const isTargetAvailable = (
  target: ActionTarget,
  nodes: NodeDefinition[],
  templateConfigsMap?: Record<number, TemplateConfigInterface>
): boolean => {
  if (!target || target.targetType == null) {
    return false
  }

  switch (target.targetType) {
    case ConversationThreadExecutionPlanActionTargetType.Node:
      return isNodeTargetAvailable(target, nodes)

    case ConversationThreadExecutionPlanActionTargetType.BusinessRule:
      return isBusinessRuleTargetAvailable(target, nodes)

    case ConversationThreadExecutionPlanActionTargetType.Expression:
      return isExpressionTargetAvailable(target, nodes, templateConfigsMap)

    case ConversationThreadExecutionPlanActionTargetType.Process:
      return isProcessTargetAvailable()

    case ConversationThreadExecutionPlanActionTargetType.CustomComponent:
      return isCustomComponentTargetAvailable(target)

    case ConversationThreadExecutionPlanActionTargetType.ReusableFunction:
      return isReusableFunctionTargetAvailable(target)

    case ConversationThreadExecutionPlanActionTargetType.ReusableUiTemplate:
      return isReusableUiTemplateTargetAvailable(target)

    case ConversationThreadExecutionPlanActionTargetType.ProjectDataModel:
      return isProjectDataModelTargetAvailable()

    default:
      return false
  }
}
